import React from "react";

type Accent = "purple" | "blue" | "orange" | "green" | "red";

type LeadStatCardProps = {
  title: string;
  value: number;
  accent?: Accent | string;
  /** Optional real trend string, e.g. "12.3%" or "-4.2%". Fake placeholders should not be passed. */
  trend?: string | null;
  icon: React.ReactNode;
  loading?: boolean;
  className?: string;
};

const ACCENTS: Record<string, { bg: string; spark: string; icon: string }> = {
  purple: { bg: "bg-[#F9F0F4]", spark: "#A02448", icon: "text-[#A02448]" },
  blue: { bg: "bg-[#EFF6FF]", spark: "#3B82F6", icon: "text-[#3B82F6]" },
  orange: { bg: "bg-[#FFF7ED]", spark: "#F59E0B", icon: "text-[#F59E0B]" },
  green: { bg: "bg-[#ECFDF5]", spark: "#10B981", icon: "text-[#10B981]" },
  red: { bg: "bg-[#FEF2F2]", spark: "#EF4444", icon: "text-[#EF4444]" },
};

/**
 * KPI card for lead list pages. Shows 0 when empty (no "No data" label).
 * Do not pass hardcoded fake trends — only real deltas.
 */
const LeadStatCard: React.FC<LeadStatCardProps> = ({
  title,
  value,
  accent = "purple",
  trend,
  icon,
  loading,
  className = "",
}) => {
  const s = ACCENTS[accent] || ACCENTS.purple;
  const numeric = Number(value || 0);
  const hasData = numeric > 0;
  const trendLabel =
    trend && String(trend).trim() ? String(trend).replace(/^-/, "") : null;
  const trendUp = !String(trend || "").startsWith("-");

  return (
    <div
      className={`flex min-w-0 flex-1 flex-col rounded-xl border border-[#EEF0F5] bg-white p-3 shadow-[0_4px_20px_rgba(15,23,42,0.04)] screen:rounded-2xl screen:p-4 ${className}`}
    >
      <div className="flex items-start gap-2.5 screen:gap-3">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl screen:h-11 screen:w-11 ${s.bg} ${s.icon}`}
        >
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium text-[#374151] screen:text-sm">
            {title}
          </p>
          {loading ? (
            <div className="mt-2 h-7 w-14 animate-pulse rounded-lg bg-[#F3F4F6] screen:h-8 screen:w-16" />
          ) : (
            <p className="mt-1 text-xl font-bold leading-none tracking-tight text-[#111827] screen:text-[26px]">
              {numeric.toLocaleString()}
            </p>
          )}
          {hasData && trendLabel ? (
            <p
              className={`mt-1.5 text-[11px] font-semibold screen:mt-2 screen:text-xs ${
                trendUp ? "text-emerald-600" : "text-red-500"
              }`}
            >
              {trendUp ? "↑" : "↓"} {trendLabel}{" "}
              <span className="hidden font-normal text-[#9CA3AF] wide:inline">
                vs last week
              </span>
            </p>
          ) : null}
        </div>
      </div>

      {hasData ? (
        <div className="mt-2 hidden w-full screen:mt-3 screen:block">
          <svg
            className="h-8 w-full"
            viewBox="0 0 120 28"
            preserveAspectRatio="none"
            fill="none"
            aria-hidden
          >
            <path
              d="M0 20 C12 18, 20 8, 32 12 S50 24, 62 16 S80 4, 92 10 S108 18, 120 14"
              stroke={s.spark}
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
              opacity="0.9"
            />
            <path
              d="M0 20 C12 18, 20 8, 32 12 S50 24, 62 16 S80 4, 92 10 S108 18, 120 14 L120 28 L0 28 Z"
              fill={s.spark}
              opacity="0.12"
            />
          </svg>
        </div>
      ) : null}
    </div>
  );
};

export default LeadStatCard;
